import React from "react";
import { Box, Grid, Paper, Typography, Stack, Chip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, Cell, LabelList,
} from "recharts";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { ticketService } from "../api/services/ticketService";
import { PageHeader, Loader } from "../components/Common";
import { useTheme } from "../contexts/ThemeContext";

// Fixed hue -> semantic role, consistent with the rest of the app's brand
// palette (cyan/purple/green/amber/red). Order chosen so adjacent chart
// series never land next to their nearest-hue neighbour (blue/purple).
const PRIORITY_TONE = { Critical: "red", Urgent: "red", High: "amber", Medium: "cyan", Low: "green" };
const FALLBACK_TONES = ["cyan", "amber", "green", "purple", "red"];

// Every company configures its own workflow (Admin -> Workflow), so the
// dashboard can't hardcode status names -- state_group is the one thing
// every status still carries, so it's what picks the card's look.
const STATE_GROUP_STYLE = {
  open: { tone: "cyan", Icon: LockOpenOutlinedIcon },
  pending: { tone: "amber", Icon: HourglassBottomOutlinedIcon },
  resolved: { tone: "green", Icon: CheckCircleOutlineIcon },
  closed: { tone: "purple", Icon: TaskAltOutlinedIcon },
};

function StatCard({ label, value, sub, subTone, Icon, tone, T }) {
  const accent = T[tone];
  const soft = T[`${tone}_soft`];
  return (
    <Paper variant="outlined" sx={{ p: 2.5, position: "relative", overflow: "hidden", height: "100%" }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent }} />
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, lineHeight: 1.1 }}>{value ?? "-"}</Typography>
          {sub && (
            <Typography variant="caption" sx={{ color: subTone ? T[subTone] : "text.secondary", fontWeight: 700 }}>
              {sub}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box sx={{
            width: 40, height: 40, borderRadius: "10px", background: soft,
            display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0,
          }}>
            <Icon fontSize="small" />
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function ChartTooltip({ active, payload, label, T }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 2, p: 1.25, boxShadow: T.shadow_soft }}>
      <Typography variant="caption" sx={{ color: T.muted, fontWeight: 700, display: "block", mb: 0.5 }}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.dataKey} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
          <Typography variant="caption" sx={{ color: T.text }}>{p.name}: <b>{p.value}</b></Typography>
        </Stack>
      ))}
    </Box>
  );
}

export default function Dashboard() {
  const { T } = useTheme();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: ticketService.dashboardSummary,
  });

  if (isLoading) return <Loader label="Loading dashboard..." />;

  const priorityData = Object.entries(data?.by_priority || {}).map(([name, count]) => ({ name, count }));
  const trendData = data?.trend || [];
  const slaBreached = data?.sla_breached ?? 0;
  const statusCards = data?.by_status || [];

  return (
    <Box>
      <PageHeader title="Dashboard" subtitle="Live ticket metrics across your workspace" />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Ticket Trend</Typography>
              <Chip label="Last 6 months" size="small" sx={{ background: T.card2, color: T.muted, fontWeight: 600 }} />
            </Stack>
            {trendData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No trend data yet.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData} margin={{ left: -20 }}>
                  <defs>
                    <linearGradient id="createdGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={T.cyan} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={T.cyan} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="resolvedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={T.green} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={T.green} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 12, fill: T.muted }} axisLine={{ stroke: T.border }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: T.muted }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip T={T} />} />
                  <Legend wrapperStyle={{ fontSize: 12, color: T.muted }} />
                  <Area type="monotone" dataKey="created" name="Created" stroke={T.cyan} strokeWidth={2}
                    fill="url(#createdGrad)" dot={{ r: 3, fill: T.cyan, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  <Area type="monotone" dataKey="resolved" name="Resolved" stroke={T.green} strokeWidth={2}
                    fill="url(#resolvedGrad)" dot={{ r: 3, fill: T.green, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Tickets by Priority</Typography>
            {priorityData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No priority data yet.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={priorityData} margin={{ left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: T.muted }} axisLine={{ stroke: T.border }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: T.muted }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip T={T} />} cursor={{ fill: T.surface_faint }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56}>
                    {priorityData.map((entry, idx) => (
                      <Cell key={entry.name} fill={T[PRIORITY_TONE[entry.name] || FALLBACK_TONES[idx % FALLBACK_TONES.length]]} />
                    ))}
                    <LabelList dataKey="count" position="top" fill={T.text} fontSize={12} fontWeight={700} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Total -> one card per workflow status, in the company's configured
          order -> SLA Breached last. Status cards are fully driven by
          whatever's configured in Admin -> Workflow, so this row reflows
          automatically as statuses are added, renamed, or reordered. */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard T={T} tone="cyan" Icon={ConfirmationNumberOutlinedIcon}
            label="Total Tickets" value={data?.total_tickets} />
        </Grid>
        {statusCards.map((s, idx) => {
          const style = STATE_GROUP_STYLE[s.state_group]
            || { tone: FALLBACK_TONES[idx % FALLBACK_TONES.length], Icon: ConfirmationNumberOutlinedIcon };
          return (
            <Grid item xs={12} sm={6} md={2} key={s.id}>
              <StatCard T={T} tone={style.tone} Icon={style.Icon} label={s.name} value={s.count} />
            </Grid>
          );
        })}
        <Grid item xs={12} sm={6} md={2}>
          <StatCard T={T} tone="red" Icon={WarningAmberOutlinedIcon}
            label="SLA Breached" value={slaBreached}
            sub={slaBreached > 0 ? "Needs attention" : "All clear"}
            subTone={slaBreached > 0 ? "red" : "green"} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <StatCard T={T} tone="cyan" Icon={SpeedOutlinedIcon}
            label="Avg. Response Time"
            value={data?.avg_response_hours != null ? `${data.avg_response_hours}h` : "-"} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard T={T} tone="purple" Icon={TimerOutlinedIcon}
            label="Avg. Resolution Time"
            value={data?.avg_resolution_hours != null ? `${data.avg_resolution_hours}h` : "-"} />
        </Grid>
      </Grid>
    </Box>
  );
}
